import { useState } from 'react';

import { useProjectFiles } from '../../state/project-files.js';
import { Icon } from '../ui/icons.js';
import {
  cellText,
  columnDefault,
  isValidName,
  newDescriptor,
  normalizeProp,
  parseCell,
  PROP_TYPES,
  type CategoryRow,
  type ProcgenDoc,
  type PropInput,
  type PropType,
} from './procgen-doc.js';

/**
 * A typed grid for category sets (section 3.3.2): `table` params and the
 * inline `categories` of label / weights outputs. Rows are categories (`name`
 * is implicit); columns are declared descriptors; reserved keys (`color`,
 * `weight`, `score`, `when`) get their own columns when allowed.
 */

export interface TableGridProps {
  columns: Record<string, PropInput>;
  rows: readonly unknown[];
  onRows: (rows: CategoryRow[]) => void;
  /** Absent: the column set is fixed. */
  onColumns?: (columns: Record<string, PropInput>) => void;
  /** Reserved row keys offered as columns (`color`, `weight`, ...). */
  reserved?: readonly string[];
  doc: ProcgenDoc | null;
  /** Names of a referenced set for `label` / `labels` columns (`of`). */
  setNames?: (ref: string | undefined) => string[] | null;
  /** JSON path of the rows (diagnostic focus target). */
  path?: string;
}

const RESERVED_TYPES: Record<string, PropInput> = {
  color: { type: 'color' },
  weight: { type: 'text', description: 'expression' },
  score: { type: 'text', description: 'expression' },
  when: { type: 'text', description: 'expression' },
};

function toRow(row: unknown, i: number): CategoryRow {
  if (typeof row === 'string') return { name: row };
  if (row && typeof row === 'object' && !Array.isArray(row)) {
    const r = row as Record<string, unknown>;
    return { ...r, name: typeof r.name === 'string' ? r.name : `row${i + 1}` } as CategoryRow;
  }
  return { name: `row${i + 1}` };
}

function hex(c: unknown): string {
  const v = Array.isArray(c) ? c : [0.5, 0.5, 0.5];
  const ch = (x: unknown) =>
    Math.round(Math.min(1, Math.max(0, typeof x === 'number' ? x : 0)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(v[0])}${ch(v[1])}${ch(v[2])}`;
}

function Cell({
  prop,
  value,
  onChange,
  options,
}: {
  prop: PropInput;
  value: unknown;
  onChange: (value: unknown) => void;
  options: string[] | null;
}) {
  const p = normalizeProp(prop);
  const [text, setText] = useState<string | null>(null);
  if (p.type === 'bool') {
    return <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} aria-label="value" />;
  }
  if (p.type === 'color') {
    return (
      <input
        type="color"
        value={hex(value)}
        onChange={(e) => {
          const h = e.target.value;
          onChange([1, 3, 5].map((i) => Math.round((parseInt(h.slice(i, i + 2), 16) / 255) * 1000) / 1000));
        }}
        aria-label="color"
      />
    );
  }
  if (p.type === 'label' && options) {
    const current = typeof value === 'string' ? value : '';
    return (
      <select value={current} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value="">—</option>
        {!options.includes(current) && current && <option value={current}>{current}</option>}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  const listId = p.type === 'asset' ? 'pcg-assets' : undefined;
  const shown = text ?? cellText(value);
  return (
    <input
      className={`pcg-cell-input${p.type === 'number' ? ' is-num' : ''}`}
      value={shown}
      list={listId}
      placeholder={cellText(columnDefault(p))}
      title={p.type === 'labels' && options ? `one or more of: ${options.join(', ')}` : p.description}
      onFocus={() => setText(cellText(value))}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== null && text !== cellText(value)) onChange(parseCell(p, text));
        setText(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setText(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

export function TableGrid({ columns, rows, onRows, onColumns, reserved = [], doc, setNames, path }: TableGridProps) {
  const entries = useProjectFiles((s) => s.entries);
  const list = rows.map(toRow);
  const reservedShown = reserved.filter((k) => k === 'color' || list.some((r) => r[k] !== undefined));
  const [extra, setExtra] = useState<string[]>([]);
  const reservedCols = [...new Set([...reservedShown, ...extra.filter((k) => reserved.includes(k))])];
  const colNames = Object.keys(columns);
  const [newCol, setNewCol] = useState('');
  const [newType, setNewType] = useState<PropType>('number');

  const assetSuffixes = colNames
    .map((c) => normalizeProp(columns[c]!))
    .filter((p) => p.type === 'asset')
    .flatMap((p) => (Array.isArray(p.suffix) ? p.suffix : p.suffix ? [p.suffix] : []));
  const assets = entries.filter((e) => e.kind === 'file' && (assetSuffixes.length === 0 || assetSuffixes.some((s) => e.path.endsWith(s)))).map((e) => e.path);

  const setCell = (i: number, key: string, value: unknown) => {
    const next = list.map((r, j) => {
      if (j !== i) return r;
      const copy: CategoryRow = { ...r };
      if (value === undefined) delete copy[key];
      else copy[key] = value;
      return copy;
    });
    onRows(next);
  };
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onRows(next);
  };
  const uniqueName = (base: string) => {
    const used = new Set(list.map((r) => r.name));
    if (!used.has(base)) return base;
    for (let k = 2; ; k++) if (!used.has(`${base}${k}`)) return `${base}${k}`;
  };

  return (
    <div className="pcg-grid-wrap" data-pcg-path={path}>
      {assetSuffixes.length > 0 && (
        <datalist id="pcg-assets">
          {assets.map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>
      )}
      <table className="pcg-grid">
        <thead>
          <tr>
            <th>name</th>
            {reservedCols.map((k) => (
              <th key={k} className="is-reserved" title="reserved key">
                {k}
              </th>
            ))}
            {colNames.map((c) => {
              const p = normalizeProp(columns[c]!);
              return (
                <th key={c} title={`${p.type}${p.of ? ` of ${p.of}` : ''}${p.description ? ` — ${p.description}` : ''}`}>
                  <span>{c}</span>
                  <span className="pcg-grid-type">{p.type}</span>
                  {onColumns && (
                    <button
                      type="button"
                      className="pcg-grid-x"
                      aria-label={`Remove column ${c}`}
                      onClick={() => {
                        const next = { ...columns };
                        delete next[c];
                        onColumns(next);
                        onRows(list.map(({ [c]: _drop, ...rest }) => rest as CategoryRow));
                      }}
                    >
                      ×
                    </button>
                  )}
                </th>
              );
            })}
            <th className="pcg-grid-actions" />
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => (
            <tr key={i}>
              <td>
                <input
                  className="pcg-cell-input is-name"
                  defaultValue={row.name}
                  key={row.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== row.name && isValidName(v)) setCell(i, 'name', v);
                    else e.target.value = row.name;
                  }}
                  aria-label="category name"
                />
              </td>
              {reservedCols.map((k) => (
                <td key={k}>
                  <Cell prop={RESERVED_TYPES[k] ?? { type: 'text' }} value={row[k]} onChange={(v) => setCell(i, k, v)} options={null} />
                </td>
              ))}
              {colNames.map((c) => {
                const p = normalizeProp(columns[c]!);
                const options = p.type === 'label' || p.type === 'labels' ? (setNames?.(p.of) ?? (Array.isArray(p.options) ? p.options : null)) : null;
                return (
                  <td key={c}>
                    <Cell prop={p} value={row[c]} onChange={(v) => setCell(i, c, v)} options={options} />
                  </td>
                );
              })}
              <td className="pcg-grid-actions">
                <button type="button" className="ibtn" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                  <Icon name="chevronUp" size={11} />
                </button>
                <button type="button" className="ibtn" aria-label="Move down" onClick={() => move(i, 1)} disabled={i === list.length - 1}>
                  <Icon name="chevronDown" size={11} />
                </button>
                <button type="button" className="ibtn" aria-label="Remove row" onClick={() => onRows(list.filter((_, j) => j !== i))}>
                  <Icon name="trash" size={11} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pcg-grid-bar">
        <button type="button" className="pcg-btn" onClick={() => onRows([...list, { name: uniqueName(doc ? 'category' : 'row') }])}>
          <Icon name="plus" size={11} /> Row
        </button>
        {reserved
          .filter((k) => !reservedCols.includes(k))
          .map((k) => (
            <button key={k} type="button" className="pcg-btn is-quiet" onClick={() => setExtra((x) => [...x, k])}>
              + {k}
            </button>
          ))}
        {onColumns && (
          <span className="pcg-grid-newcol">
            <input placeholder="new column" value={newCol} onChange={(e) => setNewCol(e.target.value)} aria-label="new column name" />
            <select value={newType} onChange={(e) => setNewType(e.target.value as PropType)} aria-label="new column type">
              {PROP_TYPES.filter((t) => !['table', 'weights', 'noise', 'positions', 'shapes', 'polylines'].includes(t)).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="pcg-btn"
              disabled={!isValidName(newCol) || newCol in columns || reserved.includes(newCol)}
              onClick={() => {
                const d = newDescriptor(newType);
                onColumns({ ...columns, [newCol]: d });
                setNewCol('');
              }}
            >
              <Icon name="plus" size={11} /> Column
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
